import { useAppSelector } from "@quest/hooks";
import { Permission } from "@quest/slice/permissions/permission.slice";

type CrudScope = "create" | "view" | "edit" | "delete";

function hasPermission(
  permissions: Permission[] | undefined,
  resource: string,
  scope: CrudScope
): boolean {
  if (!permissions || permissions.length === 0) return false;

  const expectedName = `${resource}-${scope}`;

  return permissions.some((permission) => {
    const matchesName = permission.name === expectedName;
    const matchesResourceAndScope =
      Array.isArray(permission.resources) &&
      Array.isArray(permission.scopes) &&
      permission.resources.includes(resource) &&
      permission.scopes.includes(scope);

    return matchesName || matchesResourceAndScope;
  });
}

export const usePermissions = () => {
  const { permissions } = useAppSelector((state) => state.permissions);

  const can = (resource: string, scope: CrudScope) =>
    hasPermission(permissions, resource, scope);

  return {
    canCreate: (resource: string) => can(resource, "create"),
    canRead: (resource: string) => can(resource, "view"),
    canUpdate: (resource: string) => can(resource, "edit"),
    canDelete: (resource: string) => can(resource, "delete"),
    has: can,
  };
};
